import locationsTableUrl from '../locations/locations-table.html';
import { groupTexts } from '../constants';
import config from '../../../config';

import indexUrl from './index.html';

class ManageLocationsCtrl {
  locationsTableUrl = locationsTableUrl;
  ARCHIVING_LOCATIONS_POSITIONS_ENABLED = config.ARCHIVING_LOCATIONS_POSITIONS_ENABLED;
  DELETING_PERSONAS_ENABLED = config.DELETING_PERSONAS_ENABLED;
  EXPORT_LOCATIONS_ENABLED = config.EXPORT_LOCATIONS_ENABLED;

  constructor(
    PersonaCollection,
    ConfirmService,
    Restangular,
    RestUtils,
    UserUtils,
    Authentication,
    AlertService,
    SalesService,
    GroupsService,
    IntegrationsEducation,
    filterFilter,
    uppercaseFirstFilter,
    pluralizeFilter,
    $state,
    $parse,
    $document,
    $uibModal
  ) {
    'ngInject';

    Object.assign(this, {
      PersonaCollection,
      ConfirmService,
      Restangular,
      RestUtils,
      UserUtils,
      Authentication,
      AlertService,
      SalesService,
      GroupsService,
      IntegrationsEducation,
      filterFilter,
      uppercaseFirstFilter,
      pluralizeFilter,
      $state,
      $parse,
      $document,
      $uibModal,
    });

    this.$document = $document;
    this.cancelCounter = 0;
    this.saveCounter = 0;
    this.locationsToSave = {};
    this.locationGroups = Authentication.user.isManager
      ? ['regularLocations', 'archivedLocations']
      : ['locations'];
    this.selectedItemIds = {};
  }

  $onInit() {
    this._attachProjectedSalesTotals();
    this.$document.on('keypress', this.enterClickListener);

    setTimeout(
      () =>
        this.IntegrationsEducation.showStep(
          'toast',
          this.IntegrationsEducation.stepTypes.locations
        ),
      1000
    );
  }

  $onDestroy() {
    this.$document.off('keypress', this.enterClickListener);
  }

  get selectedGroups() {
    return this.GroupsService.getSelectedGroups(
      this.selectedItemIds,
      this.visibleItemsDict,
      this.canDeleteGroup
    );
  }

  get selectedGroupsToArchive() {
    return this.GroupsService.filterUnarchived(this.selectedGroups);
  }

  get selectedGroupsToUnarchive() {
    return this.GroupsService.filterArchived(this.selectedGroups);
  }

  get locations() {
    if (this.displayMyGroups) {
      return this.PersonaCollection.me.locations;
    }

    return this.PersonaCollection.locations;
  }

  enterClickListener = (event) => {
    if (event.which === 13 && event.shiftKey === false && this.laborLocationsForm.$dirty) {
      this.submitInlineEditing();
      event.preventDefault();
    }
  };

  _attachProjectedSalesTotals() {
    this.locations.forEach((location) => {
      if ('projectedSales' in location) {
        return;
      }

      const ctrl = this;
      Object.defineProperty(location, 'projectedSales', {
        get() {
          return ctrl.SalesService.defaultSalesTotals[location.id];
        },
      });
    });
  }

  updateBudget(budget, locationId) {
    this.updateLocation(locationId, 'budget', budget);
  }

  updateHoursCap(hoursCap, locationId) {
    this.updateLocation(locationId, 'hoursCap', hoursCap);
  }

  updateLocation(locationId, field, value) {
    const locationToSave = this.locationsToSave[locationId];
    const location = this.visibleItemsDict[locationId];

    this.locationsToSave[locationId] = {
      ...(locationToSave || location),
      [field]: value,
    };
  }

  cancelInlineEditing() {
    this.laborLocationsForm.$setPristine();
    this.cancelCounter++;
    this.locationsToSave = {};
  }

  async submitInlineEditing() {
    if (this.laborLocationsForm.$invalid) {
      this.laborLocationsForm.$setSubmitted();
      throw 'Form invalid';
    }

    this.saveCounter++;

    const locationIds = Object.keys(this.locationsToSave);
    if (!locationIds.length) {
      return false;
    }

    const promises = locationIds.map((locationId) => {
      const updatedLocation = this.locationsToSave[locationId];
      return this.Restangular.restangularizeElement(null, updatedLocation, 'groups')
        .withHttpConfig()
        .put();
    });

    if (promises.length) {
      await Promise.all(promises);
      if (promises.length > 1) {
        this.AlertService.success(`${promises.length} locations' labor data has been changed`);
      } else {
        this.AlertService.success("Location's labor data has been changed");
      }
    }

    this.laborLocationsForm.$setPristine();
  }

  get selectedItemIds() {
    return this.locationGroups.reduce(
      (acc, locationGroup) => ({ ...acc, ...this[`selectedItemIds-${locationGroup}`] }),
      {}
    );
  }

  set selectedItemIds(val) {
    if (Object.keys(val).length) {
      throw 'Cannot set selectedItemIds to a non-empty object';
    }

    if (this.locationGroups) {
      this.locationGroups.forEach((locationGroup) => {
        this[`selectedItemIds-${locationGroup}`] = Object.create(null);
      });
    }
  }

  get isSomethingSelected() {
    return this.locationGroups.some((g) => this[`isSomethingSelected-${g}`]);
  }

  get visibleItemsDict() {
    return this.locationGroups.reduce(
      (acc, locationGroup) => ({ ...acc, ...this[`visibleItemsDict-${locationGroup}`] }),
      {}
    );
  }

  deleteSelectedLocations() {
    this.ConfirmService.ask(
      {
        question: groupTexts.location.multipleDeletion.title,
        conditions: groupTexts.location.multipleDeletion.conditions,
        title: 'Delete locations',
        confirm: 'Delete',
        warning: 'Warning: this cannot be undone.',
        confirmText: groupTexts.location.multipleDeletion.confirmText,
      },
      () => {
        const ids = Object.keys(this.selectedItemIds).filter(
          (groupId) =>
            this.selectedItemIds[groupId] &&
            groupId in this.visibleItemsDict &&
            this.canDeleteGroup(groupId)
        );
        this.Restangular.all('groups')
          .customDELETE(null, { id: ids })
          .then(() => {
            const text = `${ids.length} location${ids.length === 1 ? '' : 's'} deleted`;
            this.AlertService.success(text);
            this.selectedItemIds = Object.create(null);
          });
      }
    ).ignoreCancel();
  }

  async archiveSelectedLocations() {
    await this.GroupsService.archiveGroups(this.selectedGroupsToArchive, 'location');
    this.selectedItemIds = Object.create(null);
  }

  async unarchiveSelectedLocations() {
    await this.GroupsService.unarchiveGroups(this.selectedGroupsToUnarchive, 'location');
    this.selectedItemIds = Object.create(null);
  }

  canDeleteGroup = (groupId) =>
    this.UserUtils.canEditGroup(this.PersonaCollection.personasDict[groupId]);

  getDataForExport = () => {
    const activeLocations = this.PersonaCollection.locations.filter(
      (location) => location.id in this['visibleItemsDict-regularLocations']
    );
    const archivedLocations = this.PersonaCollection.archivedLocations.filter(
      (location) => location.id in this['visibleItemsDict-archivedLocations']
    );

    /**
     * Required fields:
        Location name
        External ID
        Contact name
        Contact phone
        Address
        Note
        Timezone
     */

    const fields = ['name', 'externalId', 'address', 'timezone', 'phone', 'contact', 'notes'];

    const fieldNames = {
      name: 'Name',
      externalId: 'External ID',
      address: 'Address',
      timezone: 'Time zone',
      phone: 'Phone',
      contact: 'Contact',
      notes: 'notes',
    };

    const mapField = (location, field) => {
      if (field === 'phone' && location[field]) {
        return `${location.countryCode} ${location[field]}`.trim();
      }

      return location[field];
    };

    const header = fields.map((f) => fieldNames[f].toUpperCase());

    let body = [];
    if (archivedLocations.length) {
      body.push(fields.map((f, i) => (i === 0 ? 'Active locations' : '')));
    }

    body.push(header);

    body = body.concat(
      activeLocations.map((location) => fields.map((f) => mapField(location, f) || ''))
    );

    if (archivedLocations.length) {
      body.push(fields.map(() => ''));
      body.push(fields.map((f, i) => (i === 0 ? 'Archived locations' : '')));
      body.push(header);
    }

    body = body.concat(
      archivedLocations.map((location) => fields.map((f) => mapField(location, f) || ''))
    );

    return { body };
  };

  onLocationsImported = (importedLocations) => {
    importedLocations.forEach((location) => {
      if (this.PersonaCollection.personasDict[location.id]) {
        this.PersonaCollection.editGroup(location);
      } else {
        this.PersonaCollection.addGroup(location);
      }
    });
  };
}

export default {
  controller: ManageLocationsCtrl,
  controllerAs: 'ctrl',
  templateUrl: indexUrl,
  bindings: {
    disableBulkActions: '<?',
    hideHeader: '<?',
    showBudget: '<?',
    displayMyGroups: '<?',
    columnSubset: '<?',
    search: '=?',
    includeArchived: '<?',
  },
};
